import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PaperPlaneTilt, PencilSimple, CheckCircle, X, Eye, FilePdf, Hammer, FileText, Folder } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  envoye:           { label: 'Envoyée', color: '#1565C0', bg: '#E3F2FD' },
  chiffrage_recu:   { label: 'Chiffrage reçu', color: '#92400E', bg: '#FEF3C7' },
  valide:           { label: 'Validée', color: '#2E7D32', bg: '#E8F5E9' },
  refuse:           { label: 'Refusée', color: '#C62828', bg: '#FFEBEE' },
};
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.envoye;
  return <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600, backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
};

const CORPS_OPTIONS = [
  'Démolition', 'Maçonnerie / Gros œuvre', 'Plâtrerie / Cloisons', 'Plomberie', 'Électricité',
  'Chauffage / Climatisation', 'Menuiserie', 'Serrurerie / Métallerie', 'Carrelage / Faïence',
  'Peinture', 'Sols souples', 'Couverture / Étanchéité', 'Façade / Ravalement', 'Nettoyage fin de chantier',
];

export const ProjectSubcontractorsTab = ({ project }) => {
  const [subTab, setSubTab] = useState('consultation'); // consultation | tracking
  const [consultations, setConsultations] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tracking, setTracking] = useState({ tracking: [], total_quoted_ht: 0, total_invoiced_ht: 0, total_remaining_ht: 0 });
  const [loading, setLoading] = useState(true);

  // Modal state for create/edit consultation
  const [modal, setModal] = useState({ open: false, editing: null });
  const [showComparison, setShowComparison] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [cRes, scRes, qRes, dRes, tRes] = await Promise.all([
        axios.get(`${API_URL}/api/subcontractors/consultations/list`, { params: { project_id: project.id }, withCredentials: true }),
        axios.get(`${API_URL}/api/subcontractors`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/quotes`, { params: { project_id: project.id }, withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${project.id}/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/subcontractors/project/${project.id}/tracking`, { withCredentials: true }),
      ]);
      setConsultations(cRes.data || []);
      setSubcontractors(scRes.data || []);
      setQuotes(qRes.data || []);
      setDocuments(dRes.data || []);
      setTracking(tRes.data || { tracking: [], total_quoted_ht: 0, total_invoiced_ht: 0, total_remaining_ht: 0 });
    } catch (e) {
      toast.error('Erreur lors du chargement des sous-traitants');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDeleteConsultation = async (c) => {
    if (!window.confirm(`Supprimer la consultation envoyée à ${c.subcontractor_name} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/subcontractors/consultations/${c.id}`, { withCredentials: true });
      toast.success('Consultation supprimée');
      fetchAll();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleResend = async (c) => {
    if (!c.subcontractor_email) return toast.error("Ce sous-traitant n'a pas d'email enregistré");
    try {
      await axios.post(`${API_URL}/api/subcontractors/consultations/${c.id}/send-email`, {}, { withCredentials: true });
      toast.success(`Email envoyé à ${c.subcontractor_email}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur envoi email"); }
  };

  const handleStatusChange = async (c, status) => {
    try {
      await axios.put(`${API_URL}/api/subcontractors/consultations/${c.id}`, { status }, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleAmountChange = async (c, amount) => {
    try {
      await axios.put(`${API_URL}/api/subcontractors/consultations/${c.id}`, { response_amount_ht: amount }, { withCredentials: true });
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleValidateInvoice = async (sc_id, inv_id, validation_status) => {
    let rejection_reason = '';
    if (validation_status === 'refusee') {
      rejection_reason = window.prompt('Motif du refus :', 'Avancement à mettre à jour') || '';
      if (!rejection_reason) return;
    }
    try {
      await axios.put(`${API_URL}/api/subcontractors/${sc_id}/invoices/${inv_id}/validation`, { validation_status, rejection_reason }, { withCredentials: true });
      toast.success(validation_status === 'refusee' ? 'Facture refusée — email envoyé' : 'Statut mis à jour');
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleAnalyzeInvoice = async (sc_id, inv_id) => {
    toast.message('Analyse en cours…');
    try {
      const r = await axios.post(`${API_URL}/api/subcontractors/${sc_id}/invoices/${inv_id}/analyze`, {}, { withCredentials: true });
      const parsed = r.data?.parsed || {};
      window.alert('Analyse Gemini :\n\n' + JSON.stringify(parsed, null, 2));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur Gemini'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="project-subcontractors-tab">
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E2E6', marginBottom: 18 }}>
        <button
          type="button"
          data-testid="subtab-consultation"
          onClick={() => setSubTab('consultation')}
          style={{
            padding: '10px 22px', border: 'none', background: 'transparent', cursor: 'pointer',
            fontWeight: subTab === 'consultation' ? 700 : 400,
            borderBottom: subTab === 'consultation' ? '2px solid #002FA7' : '2px solid transparent',
            marginBottom: '-2px', color: subTab === 'consultation' ? '#002FA7' : '#5E5F66',
          }}
        >Suivi consultation ST ({consultations.length})</button>
        <button
          type="button"
          data-testid="subtab-tracking"
          onClick={() => setSubTab('tracking')}
          style={{
            padding: '10px 22px', border: 'none', background: 'transparent', cursor: 'pointer',
            fontWeight: subTab === 'tracking' ? 700 : 400,
            borderBottom: subTab === 'tracking' ? '2px solid #002FA7' : '2px solid transparent',
            marginBottom: '-2px', color: subTab === 'tracking' ? '#002FA7' : '#5E5F66',
          }}
        >Suivi ST ({tracking.tracking.length})</button>
      </div>

      {subTab === 'consultation' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Demandes de chiffrage</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                data-testid="open-comparison-btn"
                onClick={() => setShowComparison(true)}
                disabled={consultations.filter(c => c.status === 'chiffrage_recu' || c.status === 'valide').length === 0}
              >📊 Comparatif des offres</button>
              <button
                className="btn btn-primary"
                data-testid="new-consultation-btn"
                onClick={() => setModal({ open: true, editing: null })}
              ><Plus size={14} /> Nouvelle demande</button>
            </div>
          </div>

          {consultations.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: '#5E5F66' }}>
              <Hammer size={40} style={{ opacity: 0.5, marginBottom: 12 }} />
              <p style={{ margin: 0 }}>Aucune demande de chiffrage envoyée</p>
              <p style={{ fontSize: '0.85rem', margin: '4px 0 0' }}>Sélectionnez des lignes du devis et envoyez-les à un sous-traitant.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {consultations.map(c => (
                <div key={c.id} className="card" style={{ padding: '14px 16px' }} data-testid={`consultation-row-${c.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                        <strong style={{ color: '#002FA7' }}>{c.subcontractor_name}</strong>
                        {c.corps_etat && <span style={{ background: '#F4F5F7', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, color: '#1F2937' }}>{c.corps_etat}</span>}
                        <StatusBadge status={c.status} />
                        {c.email_sent_at && <span style={{ fontSize: '0.72rem', color: '#2E7D32' }}>✉ envoyé {new Date(c.email_sent_at).toLocaleDateString('fr-FR')}</span>}
                        {c.submitted_at && <span style={{ fontSize: '0.72rem', color: '#1565C0', fontWeight: 600 }}>📥 chiffrage soumis {new Date(c.submitted_at).toLocaleDateString('fr-FR')}</span>}
                        {c.email_error && <span style={{ fontSize: '0.72rem', color: '#C62828' }} title={c.email_error}>⚠ erreur email</span>}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#5E5F66', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <span>{c.subcontractor_email || 'pas d\'email'}</span>
                        <span>{(c.quote_lines || []).filter(l => (l.kind || 'item') === 'item').length} ligne(s) du devis {c.quote_number || ''}</span>
                        <span>{(c.attached_documents || []).length} doc joint(s)</span>
                        {c.public_token && (
                          <button
                            type="button"
                            style={{ background: '#F4F5F7', border: '1px solid #E2E2E6', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', cursor: 'pointer', color: '#002FA7' }}
                            data-testid={`consultation-copy-link-${c.id}`}
                            onClick={() => {
                              const url = `${window.location.origin}/st/offer/${c.public_token}`;
                              navigator.clipboard.writeText(url);
                              toast.success('Lien copié dans le presse-papiers');
                            }}
                          >🔗 Copier lien portail</button>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Montant chiffré HT"
                        defaultValue={c.response_amount_ht || ''}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          if (v !== c.response_amount_ht) handleAmountChange(c, v);
                        }}
                        data-testid={`consultation-amount-${c.id}`}
                        className="form-input"
                        style={{ width: 140, height: 32, fontSize: '0.85rem', textAlign: 'right' }}
                      />
                      <Select value={c.status} onValueChange={(v) => handleStatusChange(c, v)}>
                        <SelectTrigger style={{ width: 150, height: 32 }} data-testid={`consultation-status-${c.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CFG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button
                        className="btn btn-secondary btn-sm"
                        title="Renvoyer l'email"
                        onClick={() => handleResend(c)}
                        data-testid={`consultation-resend-${c.id}`}
                      ><PaperPlaneTilt size={13} /></button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteConsultation(c)}
                        title="Supprimer"
                        data-testid={`consultation-delete-${c.id}`}
                      ><Trash size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'tracking' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Sous-traitants par corps d'état</h3>
            <div style={{ display: 'flex', gap: 18, fontSize: '0.85rem' }}>
              <div><span style={{ color: '#5E5F66' }}>Chiffré validé : </span><strong>{tracking.total_quoted_ht.toLocaleString('fr-FR')} € HT</strong></div>
              <div><span style={{ color: '#5E5F66' }}>Facturé : </span><strong style={{ color: '#1565C0' }}>{tracking.total_invoiced_ht.toLocaleString('fr-FR')} € HT</strong></div>
              <div><span style={{ color: '#5E5F66' }}>Restant : </span><strong style={{ color: tracking.total_remaining_ht > 0 ? '#2E7D32' : '#C62828' }}>{tracking.total_remaining_ht.toLocaleString('fr-FR')} € HT</strong></div>
            </div>
          </div>

          {tracking.tracking.filter(t => t.status === 'valide').length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: '#5E5F66' }}>
              <CheckCircle size={40} style={{ opacity: 0.5, marginBottom: 12 }} />
              <p style={{ margin: 0 }}>Aucun sous-traitant validé pour le moment</p>
              <p style={{ fontSize: '0.85rem', margin: '4px 0 0' }}>Validez une consultation pour qu'elle apparaisse ici.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tracking.tracking.filter(t => t.status === 'valide').map(t => (
                <div key={t.consultation_id} className="card" style={{ padding: '14px 16px' }} data-testid={`tracking-row-${t.consultation_id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
                    <div>
                      <strong style={{ color: '#002FA7' }}>{t.subcontractor_name}</strong>
                      {t.corps_etat && <span style={{ marginLeft: 10, background: '#F4F5F7', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, color: '#1F2937' }}>{t.corps_etat}</span>}
                      {t.doc_status && !t.doc_status.compliant && (
                        <span title={`Manquants: ${(t.doc_status.missing || []).join(', ')} | Expirés: ${(t.doc_status.expired || []).join(', ')}`} style={{ marginLeft: 10, background: '#FFEBEE', color: '#C62828', padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700 }}>
                          ⚠ Docs {(t.doc_status.expired || []).length > 0 ? 'expirés' : 'manquants'}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 18, fontSize: '0.85rem' }}>
                      <div><span style={{ color: '#5E5F66' }}>Chiffré : </span><strong>{t.quoted_ht.toLocaleString('fr-FR')} €</strong></div>
                      <div><span style={{ color: '#5E5F66' }}>Facturé : </span><strong style={{ color: '#1565C0' }}>{t.invoiced_ht.toLocaleString('fr-FR')} €</strong></div>
                      <div><span style={{ color: '#5E5F66' }}>Restant : </span><strong style={{ color: t.remaining_ht > 0 ? '#2E7D32' : '#C62828' }}>{t.remaining_ht.toLocaleString('fr-FR')} €</strong></div>
                    </div>
                  </div>
                  {t.invoices.length > 0 && (
                    <table className="data-table" data-testid={`tracking-invoices-${t.consultation_id}`}>
                      <thead><tr><th>N° Facture</th><th>Date</th><th>Description</th><th style={{ textAlign: 'right' }}>Montant HT</th><th>Validation</th><th>Statut</th><th></th></tr></thead>
                      <tbody>
                        {t.invoices.map(inv => {
                          const isRefused = inv.validation_status === 'refusee';
                          const docsExpired = t.doc_status && !t.doc_status.compliant;
                          const rowBg = isRefused || docsExpired ? '#FEF2F2' : 'white';
                          const validationCfg = { en_attente: { l: 'En attente', c: '#1565C0', bg: '#E3F2FD' }, validee: { l: 'Validée', c: '#2E7D32', bg: '#E8F5E9' }, refusee: { l: 'Refusée', c: '#C62828', bg: '#FFEBEE' }, payee: { l: 'Payée', c: '#047857', bg: '#D1FAE5' } }[inv.validation_status] || { l: inv.validation_status, c: '#5E5F66', bg: '#F4F5F7' };
                          return (
                            <tr key={inv.id} style={{ background: rowBg }}>
                              <td style={{ fontWeight: 600 }}>{inv.number}{inv.submitted_via === 'st_portal' && <span title="Soumise depuis le portail ST" style={{ marginLeft: 6, fontSize: '0.65rem', background: '#EFF6FF', color: '#002FA7', padding: '1px 5px', borderRadius: 3 }}>ST</span>}</td>
                              <td>{inv.date}</td>
                              <td>{inv.description}{docsExpired && <div style={{ fontSize: '0.72rem', color: '#C62828', fontWeight: 700 }}>⚠ docs expirés</div>}{isRefused && inv.rejection_reason && <div style={{ fontSize: '0.72rem', color: '#C62828' }}>Motif : {inv.rejection_reason}</div>}</td>
                              <td style={{ textAlign: 'right' }}>{inv.amount_ht.toLocaleString('fr-FR')} €</td>
                              <td><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: validationCfg.bg, color: validationCfg.c }}>{validationCfg.l}</span></td>
                              <td><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: inv.status === 'payee' ? '#E8F5E9' : '#FFF3E0', color: inv.status === 'payee' ? '#2E7D32' : '#EF6C00' }}>{inv.status === 'payee' ? 'Payée' : 'À payer'}</span></td>
                              <td>
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                  {inv.pdf_url && <a href={`${API_URL}${inv.pdf_url}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" title="Voir PDF"><FilePdf size={13} /></a>}
                                  {inv.validation_status === 'en_attente' && (
                                    <>
                                      <button className="btn btn-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }} onClick={() => handleValidateInvoice(t.subcontractor_id, inv.id, 'validee')} title="Valider" data-testid={`inv-validate-${inv.id}`}>✓</button>
                                      <button className="btn btn-sm" style={{ background: '#FFEBEE', color: '#C62828' }} onClick={() => handleValidateInvoice(t.subcontractor_id, inv.id, 'refusee')} title="Refuser" data-testid={`inv-reject-${inv.id}`}>✗</button>
                                    </>
                                  )}
                                  {inv.validation_status === 'validee' && (
                                    <button className="btn btn-sm" style={{ background: '#D1FAE5', color: '#047857' }} onClick={() => handleValidateInvoice(t.subcontractor_id, inv.id, 'payee')} title="Marquer payée" data-testid={`inv-pay-${inv.id}`}>€</button>
                                  )}
                                  {inv.pdf_url && (
                                    <button className="btn btn-sm" style={{ background: '#F3E8FF', color: '#7B1FA2' }} onClick={() => handleAnalyzeInvoice(t.subcontractor_id, inv.id)} title="Analyser avec Gemini" data-testid={`inv-analyze-${inv.id}`}>🤖</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {modal.open && (
        <ConsultationModal
          project={project}
          quotes={quotes}
          subcontractors={subcontractors}
          documents={documents}
          onClose={() => setModal({ open: false, editing: null })}
          onCreated={() => { setModal({ open: false, editing: null }); fetchAll(); }}
          onSubcontractorCreated={fetchAll}
        />
      )}

      {showComparison && (
        <ComparisonModal project={project} onClose={() => setShowComparison(false)} />
      )}
    </div>
  );
};

// ============================================================================
// Consultation creation modal
// ============================================================================

const ConsultationModal = ({ project, quotes, subcontractors, documents, onClose, onCreated, onSubcontractorCreated }) => {
  const [step, setStep] = useState(1); // 1=select lines, 2=ST+docs+send
  const [selectedQuoteId, setSelectedQuoteId] = useState(quotes[0]?.id || '');
  const [selectedLineIds, setSelectedLineIds] = useState(new Set());
  const [scId, setScId] = useState('');
  const [corpsEtat, setCorpsEtat] = useState('');
  const [message, setMessage] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showNewST, setShowNewST] = useState(false);

  const currentQuote = useMemo(() => quotes.find(q => q.id === selectedQuoteId), [quotes, selectedQuoteId]);
  const lines = currentQuote?.lines || [];

  const toggleLine = (id) => {
    setSelectedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleDoc = (id) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!scId) return toast.error('Sélectionnez un sous-traitant');
    if (selectedLineIds.size === 0) return toast.error('Sélectionnez au moins une ligne du devis');
    setSubmitting(true);
    try {
      const quoteLines = lines.filter(l => selectedLineIds.has(l.id));
      const attached = documents.filter(d => selectedDocIds.has(d.id)).map(d => ({
        doc_id: d.id, filename: d.filename, original_filename: d.original_filename, url: d.url, folder: d.folder || '',
      }));
      await axios.post(`${API_URL}/api/subcontractors/consultations`, {
        project_id: project.id,
        subcontractor_id: scId,
        corps_etat: corpsEtat,
        quote_id: currentQuote?.id || '',
        quote_number: currentQuote?.number || '',
        quote_lines: quoteLines,
        attached_documents: attached,
        message,
        send_email: sendEmail,
      }, { withCredentials: true });
      toast.success(sendEmail ? 'Consultation créée et envoyée' : 'Consultation enregistrée');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 880, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Nouvelle demande de chiffrage — Étape {step}/2</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="consultation-modal-close"><X size={20} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {step === 1 && (
            <>
              <p style={{ marginTop: 0, color: '#5E5F66', fontSize: '0.9rem' }}>Sélectionnez le devis source et cochez les lignes à envoyer pour chiffrage.</p>
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Devis source</label>
                <Select value={selectedQuoteId} onValueChange={setSelectedQuoteId}>
                  <SelectTrigger data-testid="consultation-quote-select"><SelectValue placeholder="Sélectionner un devis" /></SelectTrigger>
                  <SelectContent>
                    {quotes.map(q => (
                      <SelectItem key={q.id} value={q.id}>{q.number} — {q.date} ({q.lines?.length || 0} lignes)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!currentQuote ? (
                <p style={{ color: '#5E5F66' }}>Aucun devis disponible pour ce chantier.</p>
              ) : (
                <div style={{ border: '1px solid #E2E2E6', borderRadius: 8, overflow: 'hidden' }}>
                  <table className="data-table" data-testid="consultation-lines-table">
                    <thead><tr><th style={{ width: 40 }}></th><th>Désignation</th><th style={{ textAlign: 'center', width: 80 }}>Unité</th><th style={{ textAlign: 'right', width: 80 }}>Qté</th><th style={{ textAlign: 'right', width: 100 }}>PU HT</th></tr></thead>
                    <tbody>
                      {lines.map((ln) => {
                        const kind = (ln.kind || 'item');
                        if (kind === 'header') {
                          return (
                            <tr key={ln.id} style={{ background: '#002FA7', color: 'white' }}>
                              <td colSpan={5} style={{ padding: '6px 10px', fontWeight: 700 }}>{ln.description}</td>
                            </tr>
                          );
                        }
                        if (kind === 'note') {
                          return (
                            <tr key={ln.id} style={{ background: '#FFFBEB', color: '#92400E' }}>
                              <td colSpan={5} style={{ padding: '6px 10px', fontStyle: 'italic' }}>{ln.description}</td>
                            </tr>
                          );
                        }
                        const checked = selectedLineIds.has(ln.id);
                        return (
                          <tr key={ln.id} data-testid={`consultation-line-${ln.id}`} style={{ background: checked ? '#EFF6FF' : 'white' }}>
                            <td><input type="checkbox" checked={checked} onChange={() => toggleLine(ln.id)} data-testid={`consultation-line-cb-${ln.id}`} /></td>
                            <td>{ln.description}</td>
                            <td style={{ textAlign: 'center' }}>{ln.unit}</td>
                            <td style={{ textAlign: 'right' }}>{ln.quantity}</td>
                            <td style={{ textAlign: 'right' }}>{(ln.unit_price || 0).toLocaleString('fr-FR')} €</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p style={{ fontSize: '0.85rem', color: '#5E5F66', marginTop: 8 }}>{selectedLineIds.size} ligne(s) sélectionnée(s).</p>
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Sous-traitant *</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Select value={scId} onValueChange={setScId}>
                    <SelectTrigger data-testid="consultation-st-select" style={{ flex: 1 }}><SelectValue placeholder="Sélectionner un sous-traitant" /></SelectTrigger>
                    <SelectContent>
                      {subcontractors.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name} {s.email ? `— ${s.email}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button type="button" className="btn btn-secondary btn-sm" data-testid="consultation-new-st-btn" onClick={() => setShowNewST(true)}><Plus size={14} /> Nouveau</button>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Corps d'état</label>
                <Select value={corpsEtat} onValueChange={setCorpsEtat}>
                  <SelectTrigger data-testid="consultation-corps-select"><SelectValue placeholder="(facultatif)" /></SelectTrigger>
                  <SelectContent>
                    {CORPS_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Message complémentaire (optionnel)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Précisions techniques, délai, contraintes du chantier..."
                  data-testid="consultation-message"
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">
                  <Folder size={14} style={{ display: 'inline-block', verticalAlign: -2, marginRight: 6 }} />
                  Documents à joindre ({documents.length} disponibles dans l'onglet Documents)
                </label>
                {documents.length === 0 ? (
                  <p style={{ color: '#9E9EA7', fontSize: '0.85rem', margin: 0 }}>Aucun document disponible. Uploadez d'abord des plans / cahier des charges dans l'onglet Documents.</p>
                ) : (
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #E2E2E6', borderRadius: 8, padding: 8 }}>
                    {documents.map(d => (
                      <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', cursor: 'pointer' }} data-testid={`consultation-doc-${d.id}`}>
                        <input type="checkbox" checked={selectedDocIds.has(d.id)} onChange={() => toggleDoc(d.id)} data-testid={`consultation-doc-cb-${d.id}`} />
                        <FileText size={14} color="#5E5F66" />
                        <span style={{ fontSize: '0.85rem', flex: 1 }}>{d.title || d.original_filename}</span>
                        <span style={{ fontSize: '0.72rem', color: '#9E9EA7' }}>{d.category}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="consultation-send-email" />
                Envoyer l'email immédiatement après création
              </label>
            </>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step === 2 && <button type="button" className="btn btn-secondary" onClick={() => setStep(1)} data-testid="consultation-back">← Étape 1</button>}
            {step === 1 && <button
              type="button"
              className="btn btn-primary"
              onClick={() => setStep(2)}
              disabled={selectedLineIds.size === 0}
              data-testid="consultation-next"
            >Étape suivante →</button>}
            {step === 2 && <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="consultation-submit"
            >{submitting ? 'Envoi…' : (sendEmail ? 'Créer et envoyer' : 'Créer')}</button>}
          </div>
        </div>
      </div>

      {showNewST && (
        <SubcontractorQuickCreate
          onClose={() => setShowNewST(false)}
          onCreated={async (newId) => {
            setShowNewST(false);
            await onSubcontractorCreated();
            setScId(newId);
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// Quick subcontractor creation
// ============================================================================

const SubcontractorQuickCreate = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', siret: '', address: '' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error('Nom requis');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/subcontractors`, form, { withCredentials: true });
      toast.success('Sous-traitant créé');
      onCreated(res.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 480, padding: 20 }}>
        <h3 style={{ margin: '0 0 14px' }}>Nouveau sous-traitant</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input className="form-input" placeholder="Nom / Raison sociale *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-st-name" />
          <input className="form-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-st-email" />
          <input className="form-input" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input className="form-input" placeholder="SIRET" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} />
          <input className="form-input" placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting} data-testid="new-st-submit">{submitting ? 'Enregistrement…' : 'Créer'}</button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSubcontractorsTab;

// ============================================================================
// Comparison modal
// ============================================================================
const ComparisonModal = ({ project, onClose }) => {
  const [data, setData] = useState(null);
  const [corpsFilter, setCorpsFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/subcontractors/project/${project.id}/offers-comparison`, {
        params: corpsFilter ? { corps_etat: corpsFilter } : {},
        withCredentials: true,
      });
      setData(r.data);
    } catch { toast.error('Erreur chargement comparatif'); }
    finally { setLoading(false); }
  }, [project.id, corpsFilter]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/subcontractors/project/${project.id}/offers-comparison/excel`, {
        params: corpsFilter ? { corps_etat: corpsFilter } : {},
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `comparatif_offres_${project.name || project.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur export'); }
  };

  const allCorps = useMemo(() => {
    const set = new Set((data?.consultations || []).map(c => c.corps_etat).filter(Boolean));
    return Array.from(set);
  }, [data]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} data-testid="comparison-modal">
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 1200, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>📊 Comparatif des offres reçues</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {allCorps.length > 0 && (
              <select className="form-input" style={{ height: 32, width: 200 }} value={corpsFilter} onChange={(e) => setCorpsFilter(e.target.value)} data-testid="comparison-corps-filter">
                <option value="">Tous les corps d'état</option>
                {allCorps.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <button className="btn btn-secondary" onClick={exportExcel} data-testid="comparison-export-excel">📥 Export Excel</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {loading ? <p>Chargement…</p> : !data || data.lines.length === 0 ? (
            <p style={{ color: '#5E5F66', textAlign: 'center' }}>Aucune offre reçue pour comparer.</p>
          ) : (
            <>
              <h4 style={{ marginTop: 0 }}>Synthèse par corps d'état</h4>
              <table className="data-table" data-testid="comparison-synthese">
                <thead><tr><th>Corps d'état</th><th>Nb ST</th><th>Validé</th><th style={{ textAlign: 'right' }}>Total min HT</th><th style={{ textAlign: 'right' }}>Total max HT</th><th style={{ textAlign: 'right' }}>Écart %</th></tr></thead>
                <tbody>
                  {data.corps_synthese.map(s => (
                    <tr key={s.corps_etat}>
                      <td><strong>{s.corps_etat}</strong></td>
                      <td>{s.st_count}</td>
                      <td>{s.st_validee}</td>
                      <td style={{ textAlign: 'right' }}>{s.total_ht_min.toLocaleString('fr-FR')} €</td>
                      <td style={{ textAlign: 'right' }}>{s.total_ht_max.toLocaleString('fr-FR')} €</td>
                      <td style={{ textAlign: 'right', color: s.delta_pct > 20 ? '#C62828' : '#5E5F66', fontWeight: 600 }}>{s.delta_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 style={{ marginTop: 28 }}>Comparatif par ligne</h4>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" data-testid="comparison-lines-table" style={{ minWidth: 800 }}>
                  <thead><tr>
                    <th>Désignation</th><th style={{ width: 60 }}>Unité</th><th style={{ width: 70, textAlign: 'right' }}>Qté</th>
                    {data.consultations.map(c => (
                      <th key={c.id} style={{ textAlign: 'right', minWidth: 130 }}>{c.st_name}<br /><span style={{ fontWeight: 400, fontSize: '0.7rem', color: '#5E5F66' }}>{c.corps_etat || '-'}</span></th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {data.lines.map((ln, idx) => (
                      <tr key={idx}>
                        <td>{ln.description}</td>
                        <td>{ln.unit}</td>
                        <td style={{ textAlign: 'right' }}>{ln.quantity}</td>
                        {data.consultations.map(c => {
                          const off = ln.offers.find(o => o.consultation_id === c.id);
                          if (!off) return <td key={c.id} style={{ textAlign: 'right', color: '#9CA3AF' }}>-</td>;
                          const isMin = off.unit_price > 0 && off.unit_price === ln.min_unit_price;
                          return (
                            <td key={c.id} style={{ textAlign: 'right', background: isMin ? '#E8F5E9' : 'white', fontWeight: isMin ? 700 : 400 }}>
                              <div>{off.total_ht.toLocaleString('fr-FR')} €</div>
                              {off.delta_vs_min_pct > 0 && <div style={{ fontSize: '0.7rem', color: '#C62828' }}>+{off.delta_vs_min_pct}%</div>}
                              {off.note && <div style={{ fontSize: '0.68rem', color: '#5E5F66', fontStyle: 'italic' }}>{off.note}</div>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr style={{ background: '#F4F5F7', fontWeight: 700 }}>
                      <td colSpan={3}>TOTAL HT</td>
                      {data.consultations.map(c => (
                        <td key={c.id} style={{ textAlign: 'right' }}>{c.total_amount_ht.toLocaleString('fr-FR')} €</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
